<template>
    <div v-if="!isGuidePage" class="content_title">
        <h2>{{ moduleTypeLabel[module.type] }}</h2>
        <div v-if="sessionModule" class="choice_mode">
            <button class="toggle_btn_option active" :class="`${sessionModule.completed_at ? 'result' : 'inplay'}`"
                >{{ moduleModeText[mode] }} 모드 {{ sessionModule.completed_at ? '완료' : '진행중' }}</button
            >
        </div>
        <div v-else class="choice_mode" :class="{ toggle_btns: module.mode.length > 1 }">
            <button
                v-for="value in [ModuleModeEnum.LECTURE, ModuleModeEnum.SELF]"
                :key="value"
                class="toggle_btn_option"
                :class="{ active: mode === value }"
                :disabled="!module.mode.includes(value)"
                @click="emit('update:mode', value)"
            >
                <input type="radio" :checked="mode === value" />
                <label>{{ moduleModeText[value] }} 모드</label>
            </button>
        </div>

        <div v-if="isSettingPage" class="settings_box_toggle" :class="{ off: !isShowDetail }">
            <button @click="toggleDetail">
                <strong>{{ $t('components.teacher.contentTitle.guide') }}</strong
                >&nbsp;<span class="divider">|</span>&nbsp;<span
                    >{{ moduleTypeLabel[module.type] }} {{ $t('components.teacher.contentTitle.guideTooltip') }}</span
                >&nbsp;<i class="fa-solid fa-chevron-up"></i>
            </button>
        </div>
    </div>

    <ContentBox :id="`module_${module.type}`" :class="{ hide: !isShowDetail }">
        <div v-if="isGuidePage" class="cms_title">
            <h2>{{ moduleTypeLabel[module.type] }}</h2>
            <div class="badges">
                <small v-for="mode in module.mode" :key="mode" class="badge_default">{{ moduleModeText[mode] }}</small>
            </div>
        </div>

        <swiper
            tag="div"
            class="lst_preview"
            :slides-per-view="4.5"
            :space-between="16"
            :navigation="true"
            :modules="[Navigation]"
            :breakpoints="{
                '1000': {
                    slidesPerView: 3.5,
                    spaceBetween: 10,
                },
                '1600': {
                    slidesPerView: 4.5,
                    spaceBetween: 16,
                },
            }"
        >
            <swiper-slide
                v-if="module.guide?.video_url"
                class="video"
                tag="a"
                style="cursor: pointer"
                @click="showActiveModal(0)"
            >
                <img :src="getModuleYoutubeThumbnail(module)" alt="" />
            </swiper-slide>
            <swiper-slide
                v-for="(image, i) in screenshotImages"
                :key="i"
                tag="a"
                style="cursor: pointer"
                @click="showActiveModal(module.guide?.video_url ? i + 1 : i)"
            >
                <img :src="image.url" alt="" />
            </swiper-slide>
        </swiper>
        <div class="lst_preview_info fr-view" v-html="module.guide?.description"></div>
    </ContentBox>

    <transition name="img-layer">
        <div v-if="isShowModal" class="modal_viewer show" @click="hideActiveModal">
            <button class="modal_close">
                <i class="fa-solid fa-xmark"></i>
            </button>

            <div class="modal_content" @click.stop>
                <swiper
                    ref="modalSwiperRef"
                    :modules="[Navigation]"
                    :loop="true"
                    :slides-per-view="1"
                    :navigation="true"
                    @swiper="onModalSwiper"
                >
                    <swiper-slide v-if="module.guide?.video_url">
                        <iframe :src="module.guide?.video_url" style="width: 100%; height: 100%"></iframe>
                    </swiper-slide>
                    <swiper-slide v-for="(image, i) in screenshotImages" :key="i">
                        <img :src="image.url" alt="" />
                    </swiper-slide>
                </swiper>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
    import { computed, nextTick, PropType, ref, watch } from 'vue';
    import { ModuleModeEnum, moduleModeText } from '@/enums/ModuleModeEnum';
    import { Module } from '@/app/api/models/module';
    import { moduleTypeLabel } from '@/enums/ModuleTypeEnum';
    import ContentBox from './ContentBox.vue';
    import { useClassroomStoreWithOut } from '@/app/stores/classroom';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { Navigation } from 'swiper/modules';
    import 'swiper/css';
    import 'swiper/css/navigation';

    const props = defineProps({
        module: {
            type: Object as PropType<Module>,
            required: true,
        },
        mode: {
            type: Number as PropType<ModuleModeEnum>,
            default: ModuleModeEnum.LECTURE,
        },
    });

    const screenshotImages = computed(() => {
        return props.module.guide?.attachments.filter((attachment) => attachment.collection === 'screenshot');
    });

    const classroomStore = useClassroomStoreWithOut();

    const emit = defineEmits(['update:mode']);

    const sessionModule = computed(() => classroomStore.sessionModule);

    const isShowModal = ref(false);
    const modalIndex = ref(0);

    const modalSwiperRef = ref();
    function onModalSwiper(swiper) {
        modalSwiperRef.value = swiper;
    }

    function showActiveModal(index: number) {
        isShowModal.value = true;
        modalIndex.value = index;
    }

    function hideActiveModal() {
        isShowModal.value = false;
        modalIndex.value = 0;
    }

    function getModuleDetail() {
        const moduleDetails = localStorage.getItem('module_details') || '{}';
        const moduleDetailsObj = JSON.parse(moduleDetails);
        return moduleDetailsObj[props.module.type] ?? false;
    }

    function setModuleDetail(isShowDetail: boolean) {
        const moduleDetails = localStorage.getItem('module_details') || '{}';
        const moduleDetailsObj = JSON.parse(moduleDetails);
        moduleDetailsObj[props.module.type] = isShowDetail;
        localStorage.setItem('module_details', JSON.stringify(moduleDetailsObj));
    }

    function toggleDetail() {
        isShowDetail.value = !isShowDetail.value;

        setModuleDetail(isShowDetail.value);
    }

    const isSettingPage = computed(
        () =>
            route().current() === 'app.teacher.module.setting' ||
            route().current() === 'app.teacher.module.setting.preview',
    );

    const isGuidePage = computed(() => route().current() === 'pre-setting-modules');

    /**
     * 유튜브 썸네일 추출
     */
    function getModuleYoutubeThumbnail(module: Module) {
        const videoUrl = module.guide?.video_url;
        if (!videoUrl) return '';
        return `https://img.youtube.com/vi/${getModuleYoutubeCode(module)}/maxresdefault.jpg`;
    }

    /**
     * 유튜브 주소에서 코드 추출
     */
    function getModuleYoutubeCode(module: Module) {
        const videoUrl = module.guide?.video_url;
        if (!videoUrl) return '';

        const match = videoUrl.match(
            /(?:youtu\.be\/|youtube\.com\/(?:.*[?&]v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/,
        );
        return match ? match[1] : '';
    }

    /** 활동 안내 상세: 설정 페이지에선 기본이 열림 */
    const isShowDetail = ref((isSettingPage.value && getModuleDetail()) || isGuidePage.value);

    watch(
        () => modalIndex.value,
        (val) => {
            if (val) {
                nextTick(() => {
                    if (modalSwiperRef.value) {
                        modalSwiperRef.value.slideTo(val);
                    }
                });
            }
        },
    );
</script>

<style scoped>
    .img-layer-enter-active,
    .img-layer-leave-active {
        transition: opacity 0.3s ease;
    }

    .img-layer-enter-from,
    .img-layer-leave-to {
        opacity: 0;
    }
</style>
